import { createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';

import {
    ALL_ORGANIZATIONS_VALUE,
    OrganizationCombobox,
} from '@apps/web/components/admin/organization_combobox';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import { type ColumnDef } from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    useActivityLogs,
    useAdminCoreOrganizationSyncBackfill,
    useAdminOrganization,
    useSyncLogs,
    useSyncQueue,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ActivityLog,
    type SyncLog,
    type SyncQueueEntry,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/data-sync')({
    component: DataSyncPage,
    head: () => ({
        meta: [
            {
                title: 'Data Sync | Phoebe Admin',
            },
        ],
    }),
});

const syncLogColumns = (): Array<ColumnDef<SyncLog>> => {
    const columns: Array<ColumnDef<SyncLog>> = [
        {
            accessorKey: 'organization_name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Organization" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.organization_name}</div>;
            },
        },
        {
            accessorKey: 'status',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.status}</div>;
            },
        },
        {
            accessorKey: 'entity_type',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Entity Type" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.entity_type}</div>;
            },
        },
        {
            accessorKey: 'source_system',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Source System" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.source_system}</div>;
            },
        },
        {
            accessorKey: 'items_succeeded',
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Items Succeeded"
                />
            ),
            cell: ({ row }) => {
                return <div>{row.original.items_succeeded ?? 0}</div>;
            },
            enableSorting: true,
        },
        {
            accessorKey: 'items_failed',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Items Failed" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.items_failed ?? 0}</div>;
            },
            enableSorting: true,
        },
        {
            accessorKey: 'items_processed',
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Items Processed"
                />
            ),
            cell: ({ row }) => {
                return <div>{row.original.items_processed ?? 0}</div>;
            },
            enableSorting: true,
        },
        {
            accessorKey: 'started_at',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Started At" />
            ),
            cell: ({ row }) => {
                return (
                    <div>
                        {new Date(row.original.started_at).toLocaleString(
                            'en-US',
                            {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            },
                        )}
                    </div>
                );
            },
            enableSorting: true,
        },
        {
            accessorKey: 'duration_seconds',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Duration" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.duration_seconds}</div>;
            },
            enableSorting: true,
        },
    ];

    return columns;
};

const syncQueueColumns = (): Array<ColumnDef<SyncQueueEntry>> => {
    const columns: Array<ColumnDef<SyncQueueEntry>> = [
        {
            accessorKey: 'position',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="#" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.position}</div>;
            },
        },
        {
            accessorKey: 'status',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
                const status = row.original.status;
                return (
                    <div
                        className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            status === 'in_progress' &&
                                'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
                            status === 'pending' &&
                                'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/50 dark:text-yellow-300',
                        )}
                    >
                        {status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </div>
                );
            },
        },
        {
            accessorKey: 'organization_name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Organization" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.organization_name}</div>;
            },
        },
        {
            accessorKey: 'sync_type',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Sync Type" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.sync_type}</div>;
            },
        },
        {
            accessorKey: 'forced',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Forced" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.forced ? 'Yes' : 'No'}</div>;
            },
        },
        {
            accessorKey: 'created_at',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Queued At" />
            ),
            cell: ({ row }) => {
                return (
                    <div>
                        {new Date(row.original.created_at).toLocaleString(
                            'en-US',
                            {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            },
                        )}
                    </div>
                );
            },
            enableSorting: true,
        },
    ];

    return columns;
};

const activityLogColumns = (): Array<ColumnDef<ActivityLog>> => {
    const columns: Array<ColumnDef<ActivityLog>> = [
        {
            accessorKey: 'timestamp',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Timestamp" />
            ),
            cell: ({ row }) => {
                return (
                    <div>
                        {new Date(row.original.timestamp).toLocaleString(
                            'en-US',
                            {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            },
                        )}
                    </div>
                );
            },
            enableSorting: true,
        },
        {
            accessorKey: 'entity_type',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Entity Type" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.entity_type}</div>;
            },
        },
        {
            accessorKey: 'entity_id',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Entity ID" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.entity_id}</div>;
            },
        },
        {
            accessorKey: 'external_entity_id',
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="External Entity ID"
                />
            ),
            cell: ({ row }) => {
                return <div>{row.original.external_entity_id}</div>;
            },
        },
        {
            accessorKey: 'event_type',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Event Type" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.event_type}</div>;
            },
        },
        {
            accessorKey: 'source_system',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Source System" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.source_system}</div>;
            },
        },
        {
            accessorKey: 'summary',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Summary" />
            ),
            cell: ({ row }) => {
                return <div>{row.original.change_summary}</div>;
            },
        },
    ];
    return columns;
};

const PAGE_SIZE = 1000;

const hasOrganizationSelection = (organizationIds: ReadonlyArray<string>) =>
    organizationIds.length > 0;

const selectedOrganizationIds = (organizationIds: ReadonlyArray<string>) =>
    organizationIds.filter((id) => id !== ALL_ORGANIZATIONS_VALUE);

const isAllOrganizationsSelection = (organizationIds: ReadonlyArray<string>) =>
    organizationIds.includes(ALL_ORGANIZATIONS_VALUE);

const organizationIdsForRequests = (
    organizationIds: ReadonlyArray<string>,
): Array<string | undefined> => {
    if (isAllOrganizationsSelection(organizationIds)) {
        return [undefined];
    }
    return selectedOrganizationIds(organizationIds);
};

const singleSelectedOrganizationId = (
    organizationIds: ReadonlyArray<string>,
) => {
    if (isAllOrganizationsSelection(organizationIds)) {
        return null;
    }
    const selectedIds = selectedOrganizationIds(organizationIds);
    return selectedIds.length === 1 ? (selectedIds[0] ?? null) : null;
};

const describeOrganizationSelection = (
    organizationIds: ReadonlyArray<string>,
) => {
    if (isAllOrganizationsSelection(organizationIds)) {
        return 'all organizations';
    }
    const selectedCount = selectedOrganizationIds(organizationIds).length;
    if (selectedCount === 1) {
        return 'the selected organization';
    }
    return `${selectedCount} selected organizations`;
};

async function runForSelectedOrganizations(
    organizationIds: ReadonlyArray<string>,
    runForOrganization: (
        organizationId: string | undefined,
    ) => Promise<unknown>,
) {
    await Promise.all(
        organizationIdsForRequests(organizationIds).map((organizationId) =>
            runForOrganization(organizationId),
        ),
    );
}

function DataSyncPage() {
    const [authorizationSyncParams, setAuthorizationSyncParams] = useState<{
        organizationIds: Array<string>;
        startDate?: string;
        endDate?: string;
        forceSync: boolean;
    }>({
        organizationIds: [],
        forceSync: false,
    });
    const [caregiverSyncParams, setCaregiverSyncParams] = useState<{
        organizationIds: Array<string>;
        forceSync: boolean;
    }>({
        organizationIds: [],
        forceSync: false,
    });
    const [isSyncingAuthorizations, setIsSyncingAuthorizations] =
        useState(false);
    const [isSyncingCaregivers, setIsSyncingCaregivers] = useState(false);
    const [isSyncingShifts, setIsSyncingShifts] = useState(false);
    const [isSyncingClients, setIsSyncingClients] = useState(false);
    const [isSyncingCareCoordinators, setIsSyncingCareCoordinators] =
        useState(false);
    const [isGeocodingOrganization, setIsGeocodingOrganization] =
        useState(false);
    const [isBackfillingCoreSync, setIsBackfillingCoreSync] = useState(false);
    const [clientSyncParams, setClientSyncParams] = useState<{
        organizationIds: Array<string>;
        forceSync: boolean;
    }>({
        organizationIds: [],
        forceSync: false,
    });
    const [careCoordinatorSyncParams, setCareCoordinatorSyncParams] = useState<{
        organizationIds: Array<string>;
        forceSync: boolean;
    }>({
        organizationIds: [],
        forceSync: false,
    });
    const [shiftSyncParams, setShiftSyncParams] = useState<{
        organizationIds: Array<string>;
        startDate?: string;
        endDate?: string;
        daysLookback?: number;
        daysLookforward?: number;
        forceSync: boolean;
        includeCarePlans: boolean;
        syncDeltaOnly: boolean;
    }>({
        organizationIds: [],
        forceSync: false,
        includeCarePlans: false,
        syncDeltaOnly: false,
    });
    const [geocodeParams, setGeocodeParams] = useState<{
        organizationIds: Array<string>;
    }>({ organizationIds: [] });
    const [coreSyncBackfillParams, setCoreSyncBackfillParams] = useState<{
        organizationIds: Array<string>;
    }>({ organizationIds: [ALL_ORGANIZATIONS_VALUE] });
    const [serverSyncLogsPage, setServerSyncLogsPage] = useState(1);
    const [serverActivityLogsPage, setServerActivityLogsPage] = useState(1);
    const coreSyncBackfillSelectedOrganizationIds = selectedOrganizationIds(
        coreSyncBackfillParams.organizationIds,
    );
    const coreSyncBackfillUsesAllOrganizations = isAllOrganizationsSelection(
        coreSyncBackfillParams.organizationIds,
    );
    const coreSyncBackfillUsesMultiOrganization =
        !coreSyncBackfillUsesAllOrganizations &&
        coreSyncBackfillSelectedOrganizationIds.length > 1;
    const coreSyncBackfillOrganizationId = coreSyncBackfillUsesAllOrganizations
        ? undefined
        : singleSelectedOrganizationId(coreSyncBackfillParams.organizationIds);

    // Helper functions to check if fields are valid
    const AUTHORIZATION_SYNC_MAX_RANGE_DAYS = 45;
    const authorizationSyncDateRangeError = (): string | null => {
        const { startDate, endDate } = authorizationSyncParams;
        if (!startDate && !endDate) {
            return null;
        }
        if (!startDate || !endDate) {
            return 'Set both start and end dates, or leave both blank.';
        }
        if (startDate > endDate) {
            return 'Start date must be on or before end date.';
        }
        const startMs = Date.parse(`${startDate}T00:00:00Z`);
        const endMs = Date.parse(`${endDate}T00:00:00Z`);
        const inclusiveDays = Math.round((endMs - startMs) / 86_400_000) + 1;
        if (inclusiveDays > AUTHORIZATION_SYNC_MAX_RANGE_DAYS) {
            return `Range is ${inclusiveDays} days. Max is ${AUTHORIZATION_SYNC_MAX_RANGE_DAYS} days — split into multiple syncs.`;
        }
        return null;
    };
    const isAuthorizationSyncValid = () => {
        return (
            hasOrganizationSelection(authorizationSyncParams.organizationIds) &&
            authorizationSyncDateRangeError() === null
        );
    };

    const isCaregiverSyncValid = () => {
        return hasOrganizationSelection(caregiverSyncParams.organizationIds);
    };

    const isClientSyncValid = () => {
        return hasOrganizationSelection(clientSyncParams.organizationIds);
    };

    const isCareCoordinatorSyncValid = () => {
        return hasOrganizationSelection(
            careCoordinatorSyncParams.organizationIds,
        );
    };

    const isShiftSyncValid = () => {
        return (
            hasOrganizationSelection(shiftSyncParams.organizationIds) &&
            shiftSyncParams.startDate !== undefined &&
            shiftSyncParams.endDate !== undefined
        );
    };

    const isGeocodeValid = () => {
        return (
            selectedOrganizationIds(geocodeParams.organizationIds).length > 0
        );
    };

    const {
        data: coreSyncBackfillSummary,
        isLoading: isLoadingCoreSyncBackfillSummary,
        mutate: mutateCoreSyncBackfillSummary,
    } = useAdminCoreOrganizationSyncBackfill(
        coreSyncBackfillOrganizationId ?? undefined,
        {
            enabled:
                hasOrganizationSelection(
                    coreSyncBackfillParams.organizationIds,
                ) && !coreSyncBackfillUsesMultiOrganization,
        },
    );

    const {
        data: syncLogs,
        isLoading: isLoadingSyncLogs,
        mutate: mutateSyncLogs,
    } = useSyncLogs(undefined, {
        page: serverSyncLogsPage,
        perPage: PAGE_SIZE,
    });

    // Fetch organization data when organizationId is provided
    const { data: organization } = useAdminOrganization(
        singleSelectedOrganizationId(shiftSyncParams.organizationIds),
    );
    const dataSourceName = organization?.general_settings?.primary_data_source
        ? getDataSourceDisplayName(
              organization.general_settings.primary_data_source,
          )
        : 'External System';

    const {
        data: activityLogs,
        isLoading: isLoadingActivityLogs,
        mutate: mutateActivityLogs,
    } = useActivityLogs({ page: serverActivityLogsPage, perPage: PAGE_SIZE });

    const {
        data: syncQueueEntries,
        isLoading: isLoadingSyncQueue,
        mutate: mutateSyncQueue,
    } = useSyncQueue();

    const handleSyncAuthorizations = async () => {
        setIsSyncingAuthorizations(true);
        try {
            await runForSelectedOrganizations(
                authorizationSyncParams.organizationIds,
                (organizationId) =>
                    getApiClient().post({
                        url: '/admin/sync/authorizations',
                        body: {
                            organization_id: organizationId,
                            force_sync:
                                authorizationSyncParams.forceSync ?? false,
                            start_date: authorizationSyncParams.startDate,
                            end_date: authorizationSyncParams.endDate,
                        },
                    }),
            );

            toast.success(
                `Authorization sync initiated for ${describeOrganizationSelection(authorizationSyncParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateSyncQueue();
            void mutateSyncLogs();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate authorization sync';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Sync error:', error);
        } finally {
            setIsSyncingAuthorizations(false);
        }
    };

    const handleSyncCaregivers = async () => {
        setIsSyncingCaregivers(true);
        try {
            await runForSelectedOrganizations(
                caregiverSyncParams.organizationIds,
                (organizationId) =>
                    getApiClient().post({
                        url: '/admin/sync/caregivers',
                        body: {
                            organization_id: organizationId,
                            force_sync: caregiverSyncParams.forceSync ?? false,
                        },
                    }),
            );

            toast.success(
                `Caregiver sync initiated for ${describeOrganizationSelection(caregiverSyncParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateSyncQueue();
            void mutateSyncLogs();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate caregiver sync';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Sync error:', error);
        } finally {
            setIsSyncingCaregivers(false);
        }
    };

    const handleSyncClients = async () => {
        setIsSyncingClients(true);
        try {
            await runForSelectedOrganizations(
                clientSyncParams.organizationIds,
                (organizationId) =>
                    getApiClient().post({
                        url: '/admin/sync/clients',
                        body: {
                            organization_id: organizationId,
                            force_sync: clientSyncParams.forceSync ?? false,
                        },
                    }),
            );

            toast.success(
                `Client sync initiated for ${describeOrganizationSelection(clientSyncParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateSyncQueue();
            void mutateSyncLogs();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate client sync';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Sync error:', error);
        } finally {
            setIsSyncingClients(false);
        }
    };

    const handleSyncCareCoordinators = async () => {
        setIsSyncingCareCoordinators(true);
        try {
            await runForSelectedOrganizations(
                careCoordinatorSyncParams.organizationIds,
                (organizationId) =>
                    getApiClient().post({
                        url: '/admin/sync/care-coordinators',
                        body: {
                            organization_id: organizationId,
                            force_sync:
                                careCoordinatorSyncParams.forceSync ?? false,
                        },
                    }),
            );

            toast.success(
                `Care coordinator sync initiated for ${describeOrganizationSelection(careCoordinatorSyncParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateSyncQueue();
            void mutateSyncLogs();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate care coordinator sync';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Sync error:', error);
        } finally {
            setIsSyncingCareCoordinators(false);
        }
    };

    const handleSyncShifts = async () => {
        setIsSyncingShifts(true);
        try {
            await runForSelectedOrganizations(
                shiftSyncParams.organizationIds,
                (organizationId) =>
                    getApiClient().post({
                        url: '/admin/sync/shifts',
                        body: {
                            organization_id: organizationId,
                            start_date: shiftSyncParams.startDate,
                            end_date: shiftSyncParams.endDate,
                            days_lookback: shiftSyncParams.daysLookback,
                            days_lookforward: shiftSyncParams.daysLookforward,
                            force_sync: shiftSyncParams.forceSync ?? false,
                            include_care_plans:
                                shiftSyncParams.includeCarePlans ?? false,
                            sync_delta_only:
                                shiftSyncParams.syncDeltaOnly ?? false,
                        },
                    }),
            );

            toast.success(
                `Shifts sync initiated for ${describeOrganizationSelection(shiftSyncParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateSyncQueue();
            void mutateSyncLogs();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate shifts sync';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Sync error:', error);
        } finally {
            setIsSyncingShifts(false);
        }
    };

    const handleGeocodeOrganization = async () => {
        setIsGeocodingOrganization(true);
        try {
            await Promise.all(
                selectedOrganizationIds(geocodeParams.organizationIds).map(
                    (organizationId) =>
                        getApiClient().post({
                            url: '/admin/geocode/organization',
                            body: {
                                organization_id: organizationId,
                            },
                        }),
                ),
            );

            toast.success(
                `Organization geocoding initiated for ${describeOrganizationSelection(geocodeParams.organizationIds)}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initiate organization geocoding';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Geocode error:', error);
        } finally {
            setIsGeocodingOrganization(false);
        }
    };

    const handleCoreSyncBackfill = async () => {
        const organizationsSeen = coreSyncBackfillUsesMultiOrganization
            ? coreSyncBackfillSelectedOrganizationIds.length
            : (coreSyncBackfillSummary?.organizations_seen ?? 0);
        if (organizationsSeen === 0) {
            toast.error('No Core sync backfill candidates found', {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            return;
        }

        const pluralizedOrganization =
            organizationsSeen === 1 ? 'organization' : 'organizations';
        const confirmed = window.confirm(
            `Enqueue Core sync for ${organizationsSeen} ${pluralizedOrganization}?`,
        );
        if (!confirmed) {
            return;
        }

        setIsBackfillingCoreSync(true);
        try {
            const responses = await Promise.all(
                organizationIdsForRequests(
                    coreSyncBackfillParams.organizationIds,
                ).map((organizationId) =>
                    getApiClient().post({
                        url: '/admin/core-sync/backfill',
                        body: {
                            organization_id: organizationId,
                        },
                    }),
                ),
            );
            const organizationsEnqueued = responses.reduce(
                (total, response) => total + response.organizations_enqueued,
                0,
            );
            const enqueuedPluralizedOrganization =
                organizationsEnqueued === 1 ? 'organization' : 'organizations';

            toast.success(
                `Enqueued Core sync for ${organizationsEnqueued} ${enqueuedPluralizedOrganization}`,
                {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                },
            );
            void mutateCoreSyncBackfillSummary();
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to enqueue Core sync backfill';
            toast.error(errorMessage, {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
            console.error('Core sync backfill error:', error);
        } finally {
            setIsBackfillingCoreSync(false);
        }
    };

    const coreSyncBackfillLifecycleEntries = Object.entries(
        coreSyncBackfillSummary?.organizations_by_lifecycle_stage ?? {},
    );
    const coreSyncBackfillOrganizationsSeen =
        coreSyncBackfillUsesMultiOrganization
            ? coreSyncBackfillSelectedOrganizationIds.length
            : (coreSyncBackfillSummary?.organizations_seen ?? 0);
    const coreSyncBackfillCanRun =
        hasOrganizationSelection(coreSyncBackfillParams.organizationIds) &&
        coreSyncBackfillOrganizationsSeen > 0 &&
        (coreSyncBackfillUsesMultiOrganization ||
            coreSyncBackfillSummary !== undefined);

    return (
        <div className="flex flex-col p-8">
            <div className="mb-8">
                <h1 className="text-foreground text-2xl font-semibold">
                    Data Sync
                </h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Manage data synchronization with external systems
                </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Sync Caregivers
                        </CardTitle>
                        <CardDescription>
                            Sync caregiver data from all data sources.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 pb-4">
                            <Label htmlFor="caregiver-org">Organization</Label>
                            <OrganizationCombobox
                                value={caregiverSyncParams.organizationIds}
                                onValueChange={(value) =>
                                    setCaregiverSyncParams({
                                        ...caregiverSyncParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                            <Checkbox
                                id="caregiver-force-sync"
                                checked={caregiverSyncParams.forceSync}
                                onCheckedChange={(checked) => {
                                    setCaregiverSyncParams((prev) => ({
                                        ...prev,
                                        forceSync: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="caregiver-force-sync"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Force sync
                            </Label>
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSyncCaregivers}
                                disabled={
                                    isSyncingCaregivers ||
                                    !isCaregiverSyncValid()
                                }
                                className="w-full"
                            >
                                {isSyncingCaregivers ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Syncing…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Start Sync
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Sync Clients
                        </CardTitle>
                        <CardDescription>
                            Sync client data from all data sources.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 pb-4">
                            <Label htmlFor="client-org">Organization</Label>
                            <OrganizationCombobox
                                value={clientSyncParams.organizationIds}
                                onValueChange={(value) =>
                                    setClientSyncParams({
                                        ...clientSyncParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                            <Checkbox
                                id="client-force-sync"
                                checked={clientSyncParams.forceSync}
                                onCheckedChange={(checked) => {
                                    setClientSyncParams((prev) => ({
                                        ...prev,
                                        forceSync: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="client-force-sync"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Force sync
                            </Label>
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSyncClients}
                                disabled={
                                    isSyncingClients || !isClientSyncValid()
                                }
                                className="w-full"
                            >
                                {isSyncingClients ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Syncing…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Start Sync
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Sync Care Coordinators
                        </CardTitle>
                        <CardDescription>
                            Sync care coordinator data from all data sources.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 pb-4">
                            <Label htmlFor="care-coordinator-org">
                                Organization
                            </Label>
                            <OrganizationCombobox
                                value={
                                    careCoordinatorSyncParams.organizationIds
                                }
                                onValueChange={(value) =>
                                    setCareCoordinatorSyncParams({
                                        ...careCoordinatorSyncParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                            <Checkbox
                                id="care-coordinator-force-sync"
                                checked={careCoordinatorSyncParams.forceSync}
                                onCheckedChange={(checked) => {
                                    setCareCoordinatorSyncParams((prev) => ({
                                        ...prev,
                                        forceSync: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="care-coordinator-force-sync"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Force sync
                            </Label>
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSyncCareCoordinators}
                                disabled={
                                    isSyncingCareCoordinators ||
                                    !isCareCoordinatorSyncValid()
                                }
                                className="w-full"
                            >
                                {isSyncingCareCoordinators ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Syncing…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Start Sync
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            {organization
                                ? `Sync Shifts from ${dataSourceName}`
                                : 'Sync Shifts'}
                        </CardTitle>
                        <CardDescription>
                            {organization
                                ? `Sync shift data from ${dataSourceName} for the selected organization.`
                                : 'Sync shift data from all data sources for a specific date range.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="shift-org">Organization</Label>
                            <OrganizationCombobox
                                value={shiftSyncParams.organizationIds}
                                onValueChange={(value) =>
                                    setShiftSyncParams({
                                        ...shiftSyncParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="shift-start">Start Date</Label>
                                <Input
                                    id="shift-start"
                                    type="date"
                                    value={shiftSyncParams.startDate || ''}
                                    onChange={(e) =>
                                        setShiftSyncParams({
                                            ...shiftSyncParams,
                                            startDate:
                                                e.target.value || undefined,
                                        })
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="shift-end">End Date</Label>
                                <Input
                                    id="shift-end"
                                    type="date"
                                    value={shiftSyncParams.endDate || ''}
                                    onChange={(e) =>
                                        setShiftSyncParams({
                                            ...shiftSyncParams,
                                            endDate:
                                                e.target.value || undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="shift-lookback">
                                    Days Lookback
                                </Label>
                                <Input
                                    id="shift-lookback"
                                    type="number"
                                    value={shiftSyncParams.daysLookback ?? ''}
                                    onChange={(e) =>
                                        setShiftSyncParams({
                                            ...shiftSyncParams,
                                            daysLookback: e.target.value
                                                ? parseInt(e.target.value)
                                                : undefined,
                                        })
                                    }
                                    placeholder="Default: 0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="shift-lookforward">
                                    Days Lookforward
                                </Label>
                                <Input
                                    id="shift-lookforward"
                                    type="number"
                                    value={
                                        shiftSyncParams.daysLookforward ?? ''
                                    }
                                    onChange={(e) =>
                                        setShiftSyncParams({
                                            ...shiftSyncParams,
                                            daysLookforward: e.target.value
                                                ? parseInt(e.target.value)
                                                : undefined,
                                        })
                                    }
                                    placeholder="Default: 7"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="shift-force-sync"
                                checked={shiftSyncParams.forceSync}
                                onCheckedChange={(checked) => {
                                    setShiftSyncParams((prev) => ({
                                        ...prev,
                                        forceSync: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="shift-force-sync"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Force sync
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="shift-include-care-plans"
                                checked={shiftSyncParams.includeCarePlans}
                                onCheckedChange={(checked) => {
                                    setShiftSyncParams((prev) => ({
                                        ...prev,
                                        includeCarePlans: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="shift-include-care-plans"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Include care plans
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="shift-sync-delta-only"
                                checked={shiftSyncParams.syncDeltaOnly}
                                onCheckedChange={(checked) => {
                                    setShiftSyncParams((prev) => ({
                                        ...prev,
                                        syncDeltaOnly: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="shift-sync-delta-only"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Sync delta only (WellSky only)
                            </Label>
                        </div>
                        <Button
                            onClick={handleSyncShifts}
                            disabled={isSyncingShifts || !isShiftSyncValid()}
                            className="w-full"
                        >
                            {isSyncingShifts ? (
                                <React.Fragment>
                                    <Spinner className="mr-2" />
                                    Syncing…
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Start Sync
                                </React.Fragment>
                            )}
                        </Button>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Sync Authorizations
                        </CardTitle>
                        <CardDescription>
                            Sync authorization utilization data from all data
                            sources.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 pb-4">
                            <Label htmlFor="authorization-org">
                                Organization
                            </Label>
                            <OrganizationCombobox
                                value={authorizationSyncParams.organizationIds}
                                onValueChange={(value) =>
                                    setAuthorizationSyncParams({
                                        ...authorizationSyncParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4 pb-2">
                            <div className="space-y-2">
                                <Label htmlFor="authorization-start">
                                    Start Date
                                </Label>
                                <Input
                                    id="authorization-start"
                                    type="date"
                                    value={
                                        authorizationSyncParams.startDate || ''
                                    }
                                    onChange={(e) =>
                                        setAuthorizationSyncParams({
                                            ...authorizationSyncParams,
                                            startDate:
                                                e.target.value || undefined,
                                        })
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="authorization-end">
                                    End Date
                                </Label>
                                <Input
                                    id="authorization-end"
                                    type="date"
                                    value={
                                        authorizationSyncParams.endDate || ''
                                    }
                                    onChange={(e) =>
                                        setAuthorizationSyncParams({
                                            ...authorizationSyncParams,
                                            endDate:
                                                e.target.value || undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        {authorizationSyncDateRangeError() ? (
                            <p className="text-destructive text-sm">
                                {authorizationSyncDateRangeError()}
                            </p>
                        ) : (
                            <p className="text-muted-foreground text-xs">
                                Leave blank to sync the previous 30 days (today
                                inclusive). Max{' '}
                                {AUTHORIZATION_SYNC_MAX_RANGE_DAYS} days per
                                backfill — queue more syncs for longer ranges.
                            </p>
                        )}
                        <div className="flex items-center gap-2 pt-2">
                            <Checkbox
                                id="authorization-force-sync"
                                checked={authorizationSyncParams.forceSync}
                                onCheckedChange={(checked) => {
                                    setAuthorizationSyncParams((prev) => ({
                                        ...prev,
                                        forceSync: checked === true,
                                    }));
                                }}
                            />
                            <Label
                                htmlFor="authorization-force-sync"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Force sync
                            </Label>
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSyncAuthorizations}
                                disabled={
                                    isSyncingAuthorizations ||
                                    !isAuthorizationSyncValid()
                                }
                                className="w-full"
                            >
                                {isSyncingAuthorizations ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Syncing…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Start Sync
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Geocode Organization
                        </CardTitle>
                        <CardDescription>
                            Geocode all caregiver and client addresses for an
                            organization.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 pb-4">
                            <Label htmlFor="geocode-org">Organization</Label>
                            <OrganizationCombobox
                                value={geocodeParams.organizationIds}
                                onValueChange={(value) =>
                                    setGeocodeParams({
                                        ...geocodeParams,
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                            />
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleGeocodeOrganization}
                                disabled={
                                    isGeocodingOrganization || !isGeocodeValid()
                                }
                                className="w-full"
                            >
                                {isGeocodingOrganization ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Geocoding…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Start Geocoding
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Core Sync Backfill
                        </CardTitle>
                        <CardDescription>
                            Enqueue Phoebe-to-Core organization snapshots.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="core-sync-backfill-org">
                                Organization
                            </Label>
                            <OrganizationCombobox
                                value={coreSyncBackfillParams.organizationIds}
                                onValueChange={(value) =>
                                    setCoreSyncBackfillParams({
                                        organizationIds: value,
                                    })
                                }
                                placeholder="Select organization"
                                includeAllOption={true}
                            />
                        </div>
                        <div className="bg-muted/50 rounded-md border p-3 text-sm">
                            {isLoadingCoreSyncBackfillSummary ? (
                                <div className="text-muted-foreground flex items-center gap-2">
                                    <Spinner />
                                    <span>Counting organizations…</span>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    <div className="font-medium">
                                        {coreSyncBackfillOrganizationsSeen}{' '}
                                        {coreSyncBackfillUsesMultiOrganization
                                            ? 'organizations selected'
                                            : 'organizations ready'}
                                    </div>
                                    {coreSyncBackfillUsesMultiOrganization ? (
                                        <div className="text-muted-foreground text-xs">
                                            Backfill will enqueue each selected
                                            organization individually.
                                        </div>
                                    ) : coreSyncBackfillLifecycleEntries.length >
                                      0 ? (
                                        <div className="text-muted-foreground text-xs">
                                            {coreSyncBackfillLifecycleEntries
                                                .map(
                                                    ([stage, count]) =>
                                                        `${stage}: ${count}`,
                                                )
                                                .join(', ')}
                                        </div>
                                    ) : (
                                        <div className="text-muted-foreground text-xs">
                                            No non-internal organizations
                                            matched.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <Button
                            onClick={handleCoreSyncBackfill}
                            disabled={
                                isBackfillingCoreSync ||
                                isLoadingCoreSyncBackfillSummary ||
                                !coreSyncBackfillCanRun
                            }
                            className="w-full"
                        >
                            {isBackfillingCoreSync ? (
                                <React.Fragment>
                                    <Spinner className="mr-2" />
                                    Enqueuing…
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Enqueue Backfill
                                </React.Fragment>
                            )}
                        </Button>
                    </CardContent>
                </Card>
            </div>
            <div className="mt-8">
                <h2 className="text-foreground text-xl font-semibold">
                    Sync Queue
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                    Pending and in-progress syncs in the table queue.
                </p>
                <div className="mt-4">
                    <Card className="p-0">
                        {isLoadingSyncQueue ? (
                            <div className="flex h-32 items-center justify-center">
                                <div className="text-muted-foreground flex items-center gap-2">
                                    <Spinner />
                                    <span>Loading sync queue…</span>
                                </div>
                            </div>
                        ) : syncQueueEntries && syncQueueEntries.length > 0 ? (
                            <DataTable
                                columns={syncQueueColumns()}
                                data={syncQueueEntries}
                                isLoading={isLoadingSyncQueue}
                                initialSorting={[
                                    {
                                        id: 'position',
                                        desc: false,
                                    },
                                ]}
                                pageSize={50}
                            />
                        ) : (
                            <div className="text-muted-foreground flex h-32 items-center justify-center">
                                No pending or in-progress syncs
                            </div>
                        )}
                    </Card>
                </div>
            </div>
            <div className="mt-8">
                <h2 className="text-foreground text-xl font-semibold">
                    Recent Sync Logs
                </h2>
                <div className="mt-4">
                    <Card className="p-0">
                        {isLoadingSyncLogs ? (
                            <div className="flex h-32 items-center justify-center">
                                <div className="text-muted-foreground flex items-center gap-2">
                                    <Spinner />
                                    <span>Loading sync logs…</span>
                                </div>
                            </div>
                        ) : syncLogs && syncLogs.length > 0 ? (
                            <DataTable
                                columns={syncLogColumns()}
                                data={syncLogs}
                                isLoading={isLoadingSyncLogs}
                                initialSorting={[
                                    { id: 'started_at', desc: true },
                                ]}
                                pageSize={100}
                                renderToolbar={(table) => (
                                    <DataTableToolbar
                                        table={table}
                                        hideGlobalSearch={false}
                                    />
                                )}
                            />
                        ) : (
                            <div className="text-muted-foreground flex h-32 items-center justify-center">
                                No sync logs found
                            </div>
                        )}
                    </Card>
                </div>
                <div className="mt-4">
                    {/* Server-side pagination */}
                    <div className="flex items-center gap-2 border-l pl-4">
                        <span className="text-muted-foreground text-sm">
                            Batch: {serverSyncLogsPage}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setServerSyncLogsPage(serverSyncLogsPage - 1);
                                mutateSyncLogs();
                            }}
                            disabled={
                                isLoadingSyncLogs || serverSyncLogsPage === 1
                            }
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous {PAGE_SIZE}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setServerSyncLogsPage(serverSyncLogsPage + 1);
                                mutateSyncLogs();
                            }}
                            disabled={
                                isLoadingSyncLogs ||
                                !syncLogs ||
                                syncLogs.length < PAGE_SIZE
                            }
                        >
                            Next {PAGE_SIZE}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
            <div className="mt-8">
                <h2 className="text-foreground text-xl font-semibold">
                    Activity Logs
                </h2>
                <div className="mt-4">
                    <Card className="p-0">
                        {isLoadingActivityLogs ? (
                            <div className="flex h-32 items-center justify-center">
                                <div className="text-muted-foreground flex items-center gap-2">
                                    <Spinner />
                                    <span>Loading activity logs…</span>
                                </div>
                            </div>
                        ) : activityLogs &&
                          activityLogs.activity_logs.length > 0 ? (
                            <DataTable
                                columns={activityLogColumns()}
                                data={activityLogs.activity_logs}
                                isLoading={isLoadingActivityLogs}
                                initialSorting={[
                                    { id: 'timestamp', desc: true },
                                ]}
                                renderToolbar={(table) => (
                                    <DataTableToolbar
                                        table={table}
                                        hideGlobalSearch={false}
                                    />
                                )}
                                pageSize={100}
                            />
                        ) : (
                            <div className="text-muted-foreground flex h-32 items-center justify-center">
                                No activity logs found
                            </div>
                        )}
                    </Card>
                </div>
                <div className="mt-4">
                    {/* Server-side pagination */}
                    <div className="flex items-center gap-2 border-l pl-4">
                        <span className="text-muted-foreground text-sm">
                            Batch: {serverActivityLogsPage}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setServerActivityLogsPage(
                                    serverActivityLogsPage - 1,
                                );
                                mutateActivityLogs();
                            }}
                            disabled={
                                isLoadingActivityLogs ||
                                serverActivityLogsPage === 1
                            }
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous {PAGE_SIZE}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setServerActivityLogsPage(
                                    serverActivityLogsPage + 1,
                                );
                                mutateActivityLogs();
                            }}
                            disabled={
                                isLoadingActivityLogs ||
                                !activityLogs ||
                                activityLogs.activity_logs.length < PAGE_SIZE
                            }
                        >
                            Next {PAGE_SIZE}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
